<template>
    <div>
        <div v-if="!businessInfo?.businessPersonalInformation || store.state.guestLogin.activeAlert">
            <SystemAlert />
        </div>
        <div class="app-business-info" v-if="businessInfo?.businessPersonalInformation">
            <div class="app-info-row">
                <div class="app-info-group">
                    <span class="field-label">Customer ID</span>
                    <span id="customerId">{{ businessInfo.businessPersonalInformation.cid }}</span>
                </div>
                <div class="app-info-group">
                    <span class="field-label">Organization name</span>
                    <span id="organizationName">{{ businessInfo.businessPersonalInformation.organizationName }}</span>
                </div>
            </div>

            <!-- Address & Contact Info -->
            <div class="app-info-business-row">
                <div class="business-info-box">
                    <!-- Physical Address -->
                    <div class="business-user-info">
                        <span class="business-user-address-info">Physical address</span>
                        <span id="physicalAddressLine1">{{ businessInfo.businessPersonalInformation.physicalAddress.addressLine1 }}</span>
                        <span id="physicalAddressLine2">{{ businessInfo.businessPersonalInformation.physicalAddress.addressLine2 }}</span>
                        <span id="physicalAddressCity">
                            {{ businessInfo.businessPersonalInformation.physicalAddress.city }},
                            {{
                                businessInfo.businessPersonalInformation.physicalAddress.addressType === 'Us Domestic'
                                    ? businessInfo.businessPersonalInformation.physicalAddress.state
                                    : businessInfo.businessPersonalInformation.physicalAddress.province
                            }},
                            {{ businessInfo.businessPersonalInformation.physicalAddress.country }}
                        </span>
                        <span id="physicalAddressZip">
                            {{
                                businessInfo.businessPersonalInformation.physicalAddress.addressType === 'Us Domestic'
                                    ? businessInfo.businessPersonalInformation.physicalAddress.zipPlus === ''
                                        ? businessInfo.businessPersonalInformation.physicalAddress.zipCode
                                        : `${businessInfo.businessPersonalInformation.physicalAddress.zipCode} - ${businessInfo.businessPersonalInformation.physicalAddress.zipPlus}`
                                    : businessInfo.businessPersonalInformation.physicalAddress.internationalZipCode
                            }}
                        </span>
                        <div v-if="!businessInfo.businessPersonalInformation.isBusinessGroupExists">
                            <button type="button" @click.prevent="handleBusinessUpdateAddress" class="link-button">
                                Update my address
                            </button>
                        </div>
                    </div>

                    <!-- Mailing Address -->
                    <div class="business-user-info">
                        <span class="business-user-address-info">Mailing address</span>
                        <span id="mailingAddressLine1">{{ businessInfo.businessPersonalInformation.mailingAddress?.addressLine1 }}</span>
                        <span id="mailingAddressLine2">{{ businessInfo.businessPersonalInformation.mailingAddress?.addressLine2 }}</span>
                        <span id="mailingAddressCity">
                            {{ businessInfo.businessPersonalInformation.mailingAddress?.city }},
                            {{
                                businessInfo.businessPersonalInformation.mailingAddress?.addressType === 'Us Domestic'
                                    ? businessInfo.businessPersonalInformation.mailingAddress?.state
                                    : businessInfo.businessPersonalInformation.mailingAddress?.province
                            }},
                            {{ businessInfo.businessPersonalInformation.mailingAddress?.country }}
                        </span>
                        <span id="mailingAddressZip">
                            {{
                                businessInfo.businessPersonalInformation.mailingAddress?.addressType === 'Us Domestic'
                                    ? businessInfo.businessPersonalInformation.mailingAddress?.zipPlus === ''
                                        ? businessInfo.businessPersonalInformation.mailingAddress?.zipCode
                                        : `${businessInfo.businessPersonalInformation.mailingAddress?.zipCode} - ${businessInfo.businessPersonalInformation.mailingAddress?.zipPlus}`
                                    : businessInfo.businessPersonalInformation.mailingAddress?.internationalZipCode
                            }}
                        </span>
                    </div>

                    <!-- Contact Info -->
                    <div class="business-user-infos">
                        <span class="business-contact-info">Contact information</span>
                        <div class="verify-business">
                            <span class="business-contact-info">Contact name:</span>
                            <span id="contactName" class="user-info-email">{{ businessInfo.businessPersonalInformation.contactPersonName || 'No contact name on file' }}</span>
                        </div>
                        <div class="verify-business">
                            <span class="business-contact-info">Office email:</span>
                            <span id="officeEmail" class="user-info-email">
                                {{ businessInfo.businessPersonalInformation.officeEmail || 'No office email on file' }}
                                <img v-if="businessInfo.businessPersonalInformation.isOfficeEmailVerified" class="verify-icon" src="/static/verified-icon.png" alt="Email is verified">
                            </span>
                        </div>
                        <div class="verify-business">
                            <span class="business-contact-info">Cell phone:</span>
                            <span id="cellPhone" class="user-info-email">
                                {{ businessInfo.businessPersonalInformation.cellPhone || 'No cell phone on file' }}
                                <img v-if="businessInfo.businessPersonalInformation.isCellPhoneVerified" class="verify-icon" src="/static/verified-icon.png" alt="Phone is verified">
                            </span>
                        </div>
                        <div class="verify-business">
                            <span class="business-contact-info">Office phone:</span>
                            <span id="officePhone" class="user-info-email">{{ businessInfo.businessPersonalInformation.officePhone || 'No office phone on file' }}</span>
                        </div>
                        <div class="verify-business">
                            <span class="business-contact-info">Office fax:</span>
                            <span id="officeFax" class="user-info-email">{{ businessInfo.businessPersonalInformation.officeFax || 'No office fax on file' }}</span>
                        </div>
                        <div v-if="mfaType != 4">
                            <button type="button" @click.prevent="handleBusinessContactUpdate" class="link-button">
                                Update / Verify my contact information
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Reservations -->
            <div>
                <div class="res-display">
                    <span class="res-notification">Upcoming DMV Reservations</span>
                    <Tooltip theme="my-custom-tooltip" :triggers="['hover', 'click', 'focus']">
                        <input
                            type="image"
                            src="/static/RefreshBtn.png"
                            class="refresh-icon app-tooltip"
                            tabindex="0"
                            alt="Click Refresh to view updated reservation information"
                            @click="getRefresh()"
                        />
                        <span class="sr-only">Click Refresh to view updated reservation information</span>
                        <template #popper>
                            Click Refresh to view updated reservation information
                        </template>
                    </Tooltip>
                </div>
                <div class="upcoming-res">
                    <div class="app-info-row app-reservations" v-if="businessInfo.businessReservationInformation && businessInfo.businessReservationInformation.length > 0">
                        <div class="user-reservations" v-for="(reservationInformation, i) in businessInfo.businessReservationInformation" :key="i">
                            <div class="app-info-group user-res-details">
                                <span class="res-details">Service</span>
                                <span>{{ reservationInformation.visitType }}</span>
                            </div>
                            <div class="app-info-group user-res-details">
                                <span class="res-details">Confirmation number</span>
                                <span>{{ reservationInformation.appointmentNumber }}</span>
                            </div>
                            <div class="app-info-group user-res-details">
                                <span class="res-details">Date and time</span>
                                <span>{{ formatWeekday(reservationInformation.scheduleDate) }}</span>
                                <span>{{ formatMonthDayYear(reservationInformation.scheduleDate) }}</span>
                                <span>{{ formatTime(reservationInformation.scheduleTime) }}</span>
                            </div>
                            <div class="app-info-group user-res-details">
                                <span class="res-details">Location</span>
                                <span>{{ reservationInformation.location }}</span>
                            </div>
                            <a href="#" @click.prevent="modifyReservation(reservationInformation.appointmentNumber)">Modify / Cancel Reservation </a>
                        </div>
                    </div>
                    <!-- "no-reservation" block -->
                    <div class="no-reservation" v-else>
                        <span>No upcoming reservation(s) to show.</span>
                    </div>
                </div>
            </div>

            <!-- "blocks" block -->
            <div class="app-notice app-info-business-row" v-if="businessInfo?.businessBlocksInformation?.length > 0">
                <div class="blocks-info">
                    <img class="active-icon" src="/static/activeBlocks.svg" alt="Icon" />
                    <b>You have the following block(s):</b>
                </div>
                <ul>
                    <li v-for="(item, p) in businessInfo.businessBlocksInformation" :key="`action${p}`">
                        <template v-if="item.actionUrl && item.actionId == '9'">
                            <span>
                            {{ splitInstructionForLink(item.actionInstruction).before }}

                            <a :href="item.actionUrl" target="_blank">
                                {{ splitInstructionForLink(item.actionInstruction).match }}
                                <LinkOpenNewSr />
                            </a>
                            {{ splitInstructionForLink(item.actionInstruction).after }}
                            </span>
                        </template>
                        <template v-else>
                            {{ item.actionInstruction }}
                        </template>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import * as COMMONS from '../../../store/_commons';
import SystemAlert from '@/components/SystemAlert';
import LinkOpenNewSr from '../../ui/LinkOpenNewSr.vue';
import { Tooltip } from 'floating-vue';
import '@/assets/tooltip.css';
import {formatWeekday, formatMonthDayYear, formatTime} from '../../../utils/globalFilters.js';

// Vuex store
const store = useStore();

const router = useRouter(); 

// Define emits to communicate with the parent component
const emit = defineEmits(['doRefresh']);

// Reactive state for onlineToken 
const onlineToken = ref('');

// Computed properties
const mfaType = computed(() => store.state.guestLogin.mfaType);
const onlineTokenLocalStorage = computed(() => localStorage.getItem('onlineToken'));
const businessInfo = computed(() => store.getters[COMMONS.BUSINESS_PROFILE_INFO]);

// Watchers
watch(onlineToken, (value) => {
  if (value) {
    window.close();
  }
});

watch(onlineTokenLocalStorage, (value) => {
  if (value) {
    window.close();
  }
});

// Lifecycle hooks
onMounted(() => {
  window.addEventListener('storage', (event) => {
    if (event.key === 'onlineToken' && event.newValue) {
      window.close();
    }
  });
});

const getBusinessProfileInformation = () => {
    store.dispatch(COMMONS.BUSINESS_PROFILE_GET_PROFILE);
};

const splitInstructionForLink = (text) => {
  const phrase = "Insurance Revocation Reinstatement."
  if (!text) return { before: "", match: "", after: "" }

  const lowerText = text.toLowerCase()
  const lowerPhrase = phrase.toLowerCase()

  const index = lowerText.indexOf(lowerPhrase);
  if (index === -1) {
    return { before: text, match: "", after: "" }
  }

  const before = text.slice(0, index)
  const match = text.slice(index, index + phrase.length)  
  const after = text.slice(index + phrase.length)

  return { before, match, after }
};

// Methods
const handleBusinessContactUpdate = () => {
    // Clear the source so navigation works properly
    store.state.guestLogin.navigationSource = null;
    
    // Clear createContact flag 
    store.state.guestLogin.createContact = false;

    // Set the default step for contact updates
    store.state.guestLogin.contactUpdateStep = 1; 
    
    // Navigate to business contact update route
    router.push({ name: 'BusinessUpdateContacts' });
};

const handleBusinessUpdateAddress = () => {
    // Clear the source so navigation works properly
    store.state.guestLogin.navigationSource = null;

    // Navigate to business address update route
    router.push({ name: 'BusinessUpdateAddress' });
};

const modifyReservation = (appointmentNumber) => {
    localStorage.setItem('confirmationNumber', appointmentNumber);
    localStorage.setItem('onlineToken', encodeURIComponent(store.state.guestLogin.accessToken));
    onlineToken.value = localStorage.getItem('onlineToken');
    window.open(`${window.config.WEB_CLIENT_URL}lookup?confirmationNumber=${appointmentNumber}&&onlineToken=${onlineToken.value}`);
};

const getRefresh = () => {
    getBusinessProfileInformation();
    emit('doRefresh');
};

// Lifecycle hooks
onUnmounted(() => {
    localStorage.removeItem('onlineToken');
    localStorage.removeItem('confirmationNumber');
});
</script>

<style scoped lang="scss">
.app-info-row {
    display: flex;
    flex-wrap: wrap;

    .app-info-group {
        min-width: 150px;
        width: 31%;
        margin-bottom: 1.5rem;
        .field-label{
            font-weight: bold;
            margin-bottom: 0.5rem;
        }
        span {
            display: block;
            overflow-wrap: break-word;
        }
    }
    .user-res-details{
        min-width: 190px;
        margin-bottom: 0.5rem;

        .res-details {
            font-weight: bold;
            margin-bottom: 0.5rem;
        }
    }
}
@media only screen and (max-width: 600px) {
    .app-info-row {
        display: block;
        .user-res-details{
            min-width: 275px;
        }
    }
}

.app-business-info {
    display: block;
    .app-info-business-row {
        display: flex;
        flex-wrap: wrap;
        gap: 1% 3%;
        .business-info-box {
            display: flex;
            flex-wrap: wrap;
            width: 100%;
            margin: 1rem auto 2rem auto;
            box-sizing: border-box;
            flex-direction: row;
            .business-user-info {
                width: 31%;
                word-wrap: break-word;
                .business-user-address-info,
                .business-contact-info {
                    font-weight: 600;
                }
                span {
                    display: block;
                    line-height: 30px;
                    gap: 0.5rem;
                }
                .verify-business {
                    display: flex;
                    align-items: flex-start;
                    flex-wrap: wrap;
                    gap: 1%;
                    justify-content: flex-start;
                    .verify-business-email, .verify-business-phone {
                        display: flex;
                        width: auto !important;
                        flex-wrap: wrap;
                        gap: 0.5rem;    
                    }
                    .user-info-email{
                        display: block;
                        word-break: break-word;
                    }
                }
                .verify-icon {
                    width: 50px;
                    height: 20px;
                }
            }
            .link-button {
                background: none;
                border: none;
                padding: 0;
                cursor: pointer;
                font: inherit;
                color: var(--ri-blue);
                text-decoration: underline;
                display: inline-block;  
                text-align: left;  
                &:hover{
                    color: none;
                    text-decoration: none;
                }
            }
        }
    }
    .app-info-business-rows {
        display: flex;
        gap: 1% 3%;
    }
    .app-notice {
        --whitespace: 1.5em;
        margin-bottom: var(--whitespace);
        max-width: 100%;
        p:first-child {
            @extend %font-sans-bold;
        }
        ul {
          li {
            list-style: initial;
            margin-bottom: calc(var(--whitespace) * 0.5);
          }
        }
        @supports (margin-block: 1em) {
          margin-block-end: var(--whitespace);
          ul {
            margin-inline-start: var(--whitespace);
            li {
              margin-block-end: calc(var(--whitespace) * 0.5);
            }
          }
        }
    }
    .res-display{
        display: flex;
        align-items: center;
    }
    .upcoming-res{
        margin-top: 1rem;
        .no-reservation{
            margin-bottom: 0.5rem;
        }
    }
    .app-reservations {
        display: block;
        .user-reservations{
            display: flex;
            flex-wrap: wrap;
            margin-top: 0.5rem;
            margin-bottom: 0.5rem;
        }
    }
    .res-notification{
        font-weight: bold;
    }
    .refresh-icon{
        margin-left: 10px;
        height: 30px;
    }
}

@media only screen and (max-width: 700px) {
    .app-business-info .app-info-business-row{
        display: block;
        .business-info-box {
            display: block;
            .business-user-info {
                width: 100%; 
                    margin-bottom: 0.5rem;
                .verify-business {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    .verify-business-phone, .verify-business-email {
                        display: flex;
                        width: auto !important;
                        flex-wrap: wrap;
                        gap: 0.5rem;
                    }
                    .user-info-email{
                        display: block;
                        width: auto;
                        overflow: hidden;
                    }
                }
                .business-contact-info {
                    width: auto !important;
                }
            }
        }
    }
}

@media only screen and (max-width: 800px) {
    .active-icon {
        width: 25px;
        height: 25px;
        vertical-align: middle;
    }
    .blocks-info {
        margin-bottom: 1rem;
        margin-left: 0rem !important;
    }
    .app-notice{
        ul {
            li {
                margin-left: 1rem;
            }
        }
    }
}

@media only screen and (min-width: 801px) and (max-width: 1000px) {
    .app-business-info .app-info-business-row .business-info-box .business-user-info  {
        .verify-business{
            display: block;
        }
        .business-contact-info {
            width: auto !important;
        }
    }
}

.business-user-info {
    width: 31%;
    overflow-wrap: break-word;    
    span {
        display: block;
        line-height: 30px;
        gap: 0.5rem;
    }
    h2 {
        font-size: 1.25rem;
    }   
    .user-info-sub-row {
        display: flex;
        gap: 1rem 3rem;
        flex-wrap: wrap;
    } 
    .verify-phone, .verify-email{
        display: flex;
        flex-wrap: wrap;
        .verify-icon{
            margin-left: 5px;
            width: 50px;
            height: 20px;
        }
        .user-info-email{
            display: block;
            width: 100%;
        }
    } 
    .comm-text {
        display: flex;
        font-weight: 600;
        align-items: baseline;
        .btn-tooltip-trigger{
            min-width: 25px;
            border: none;
            background-color: unset;
            border-radius: unset;
            padding: 0rem;
            .comm-tooltip{
                width: 18px;
                margin-bottom: -2px;
            }
        }
    }
    .app-communication {
        display: flex;
        gap: 0.5rem;
        .comm-notenrolled {
            color: green;
        }
    }

    .link-button {
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        font: inherit;
        color: var(--ri-blue);
        text-decoration: underline;
        display: inline-block;  
        text-align: left;  
        &:hover{
            color: inherit;
            text-decoration: none;
        }
    }

}
@media only screen and (max-width: 600px) {
    .user-info {
        width: 100%;     
        .user-info-sub-row {
            display: block;
            .organ-donor {
                padding-top: 10px;
            }
        } 
        .verify-phone, .verify-email{
            .verify-icon{
                width: 50px;
                height: 20px;
            }
        }
        .comm-text {
            display: flex;
            font-weight: 600;
        }
    }  
}

@media only screen and (min-width: 601px) and (max-width: 780px) {
    .user-info {    
        .app-communication {
            display: block;
        }
    }  
}

@media only screen and (min-width: 610px) and (max-width: 950px) {
    .user-info {  
        .verify-phone, .verify-email{
            .verify-icon{
                width: 50px;
                height: 20px;
            }
        }
        .comm-text {
        display: flex;
        font-weight: 600;
        .btn-tooltip-trigger{
            min-width: 25px;
            border: none;
            background-color: unset;
            border-radius: unset;
            padding: 0rem;
            .comm-tooltip{
                width: 18px;
                margin-bottom: -2px;
            }
        }
    }
    }  
}
.business-user-infos {
    width: 38%;
    word-wrap: break-word;
    .business-user-address-info,
    .business-contact-info {
        font-weight: 600;
    }
    span {
        display: block;
        line-height: 30px;
        gap: 0.5rem;
    }
    .verify-business {
        display: flex;
        align-items: flex-start;
        flex-wrap: wrap;
        gap: 3%;
        justify-content: flex-start;
        .verify-business-email, .verify-business-phone {
            display: flex;
            width: auto !important;
            flex-wrap: wrap;
            gap: 0.5rem;    
        }
        .user-info-email{
            display: block;
            word-break: break-word;
        }
    }
    .verify-icon {
        width: 50px;
        height: 20px;
    }
    .comm-text {
        display: flex;
        font-weight: 600;
        align-items: baseline;
        .btn-tooltip-trigger{
            min-width: 25px;
            border: none;
            background-color: unset;
            border-radius: unset;
            padding: 0rem;
            .comm-tooltip{
                width: 18px;
                margin-bottom: -2px;
            }
        }
    }
    .app-communication {
        display: flex;
        gap: 0.5rem;
        .comm-notenrolled {
            color: green;
        }
    }

    .link-button {
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        font: inherit;
        color: var(--ri-blue);
        text-decoration: underline;
        display: inline-block;  
        text-align: left;  
        &:hover{
            color: inherit;
            text-decoration: none;
        }
    }
}

.active-icon {
    width: 25px;
    height: 25px;
    vertical-align: middle;
}
.blocks-info {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1rem;
    margin-left: -1rem;
    align-items: center;
}

.app-link-open {
    cursor: pointer;
    color: var(--ri-blue);
    &:focus {
        outline-color: #ff8e47;
    }
}
</style>